import React, { useEffect, useState, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { getInviteBySlug, acceptInviteBySlug } from '../api'

export default function JoinPage() {
  useEffect(() => {
    const prev = document.documentElement.getAttribute('data-theme')
    document.documentElement.setAttribute('data-theme', 'dark')
    return () => { if (prev) document.documentElement.setAttribute('data-theme', prev); else document.documentElement.removeAttribute('data-theme') }
  }, [])

  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const { session, loading: authLoading } = useAuth()

  const [invite, setInvite] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [accepting, setAccepting] = useState(false)
  const acceptedRef = useRef(false)

  useEffect(() => {
    if (!slug) return
    getInviteBySlug(slug)
      .then(r => setInvite(r.data))
      .catch(e => {
        const s = e.response?.status
        if (s === 404) setError('This invite link is invalid.')
        else if (s === 410) setError('This invitation has expired.')
        else if (s === 409) setError('This invitation has already been used.')
        else setError(e.response?.data?.detail || 'This invite link is invalid or has expired.')
      })
      .finally(() => setLoading(false))
  }, [slug])

  // Auto-accept from signup/login redirect
  useEffect(() => {
    if (!session || !invite || !slug || acceptedRef.current) return
    const pending = sessionStorage.getItem('skilflow_pending_invite')
    console.log('[Join] Auto-accept check:', { pending, slug })
    if (pending === `join:${slug}`) {
      sessionStorage.removeItem('skilflow_pending_invite')
      const userEmail = session.user?.email?.toLowerCase() || ''
      const inviteEmail = invite.email?.toLowerCase() || ''
      if (inviteEmail && userEmail && userEmail !== inviteEmail) return
      doAccept()
    }
  }, [session, invite]) // eslint-disable-line

  const doAccept = async () => {
    if (!slug || acceptedRef.current) return
    acceptedRef.current = true
    setAccepting(true)
    console.log('[Join] Accepting:', slug)
    try {
      const r = await acceptInviteBySlug(slug)
      localStorage.setItem('skilflow_inviter', invite?.inviter_name || '')
      localStorage.setItem('skilflow_invite_role', invite?.role || 'editor')
      navigate(`/welcome/${r.data.team_id}`, { replace: true })
    } catch (e: any) {
      acceptedRef.current = false
      if (e.response?.status === 409) navigate('/', { replace: true })
      else setError('Failed to accept. Please try again.')
    } finally { setAccepting(false) }
  }

  const goSignup = () => {
    if (slug) sessionStorage.setItem('skilflow_pending_invite', `join:${slug}`)
    navigate(`/signup?redirect=/join/${slug}`)
  }

  const goLogin = () => {
    if (slug) sessionStorage.setItem('skilflow_pending_invite', `join:${slug}`)
    navigate(`/login?redirect=/join/${slug}`)
  }

  if (authLoading || loading) return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ color: 'var(--text3)', fontSize: 13 }}>Loading...</div>
    </div>
  )

  const roleLabel = invite?.role ? invite.role.charAt(0).toUpperCase() + invite.role.slice(1) : ''

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ width: '100%', maxWidth: 420, textAlign: 'center' }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 32 }}>
          <svg width="28" height="28" viewBox="0 0 28 28"><rect width="28" height="28" rx="6" fill="#7c3aed"/><rect x="4" y="7" width="20" height="4.5" rx="2.25" fill="white" transform="rotate(-10,14,9.25)"/><rect x="4" y="14" width="14" height="4.5" rx="2.25" fill="white" opacity=".55" transform="rotate(-10,11,16.25)"/><rect x="4" y="21" width="8" height="4.5" rx="2.25" fill="white" opacity=".22" transform="rotate(-10,8,23.25)"/></svg>
          <span style={{ fontFamily: 'Sora, sans-serif', fontSize: 17, fontWeight: 700, color: 'var(--text1)' }}>Skil<span style={{ color: 'var(--brand-light)' }}>flow</span></span>
        </div>

        {error && (
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 16, padding: '48px 32px' }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text1)', marginBottom: 8 }}>{error}</div>
            <a href="https://skilflow.ai" style={{ color: 'var(--brand-light)', fontSize: 13, textDecoration: 'none' }}>Go to Skilflow &rarr;</a>
          </div>
        )}

        {!error && accepting && (
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 16, padding: '48px 32px' }}>
            <div style={{ color: 'var(--text3)', fontSize: 13 }}>Joining team...</div>
          </div>
        )}

        {!error && !accepting && invite && (
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 16, padding: '32px' }}>
            {invite.custom_message && (
              <div style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 10, padding: '16px 18px', marginBottom: 20, textAlign: 'left' }}>
                <div style={{ fontSize: 13, color: 'var(--text2)', fontStyle: 'italic', lineHeight: 1.6, marginBottom: 10 }}>
                  &ldquo;{invite.custom_message}&rdquo;
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 24, height: 24, borderRadius: '50%', background: 'var(--brand-dim)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: 'var(--brand-light)' }}>
                    {(invite.inviter_name || 'S')[0].toUpperCase()}
                  </div>
                  <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text2)' }}>{invite.inviter_name}</span>
                </div>
              </div>
            )}
            <div style={{ width: 44, height: 44, borderRadius: 12, background: 'var(--brand-dim)', border: '1px solid rgba(124,58,237,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#a78bfa" strokeWidth="1.5"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/></svg>
            </div>
            <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text1)', marginBottom: 6 }}>You're invited!</div>
            <div style={{ fontSize: 14, color: 'var(--text2)', marginBottom: 16 }}>
              Join <strong>{invite.team_name}</strong> as {roleLabel}
            </div>

            {session ? (
              session.user?.email && invite.email && session.user.email.toLowerCase() !== invite.email.toLowerCase() ? (
                <div style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: 10, padding: 14, textAlign: 'left', fontSize: 12, color: 'var(--text2)', lineHeight: 1.6 }}>
                  <div style={{ fontWeight: 600, color: '#f59e0b', marginBottom: 6 }}>Wrong account</div>
                  This invitation was sent to <strong style={{ color: 'var(--text1)' }}>{invite.email}</strong>. You're signed in as <strong style={{ color: 'var(--text1)' }}>{session.user.email}</strong>.
                  <div style={{ marginTop: 10 }}>Sign out first, or open this link in a private/incognito window.</div>
                </div>
              ) : (
                <button onClick={doAccept} className="btn-primary" style={{ padding: '12px 32px', fontSize: 15, width: '100%' }}>
                  Accept & join team
                </button>
              )
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <button onClick={goSignup} className="btn-primary" style={{ padding: '12px 32px', fontSize: 15, width: '100%' }}>
                  Create account
                </button>
                <button onClick={goLogin} className="btn-ghost" style={{ padding: '10px 32px', fontSize: 14, width: '100%' }}>
                  Sign in
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
